/**
 * ═══════════════════════════════════════════════════════════════
 * MIGRACIÓN: Unificar tareas en empresas/{empId}/tareas
 * ═══════════════════════════════════════════════════════════════
 *
 * Este script migra datos de dos fuentes al sistema unificado:
 *
 *   1. Colección top-level `accionables` (tareas con empresaId)
 *      → se mueven a `empresas/{empId}/tareas`
 *      NOTA: Solo docs que son TAREAS (tienen estado/responsableUserId/emisorUserId).
 *            Los docs de CATÁLOGO (nombre/objetivo/ejecutores/protocoloIds) se quedan.
 *
 *   2. Subcollección `empresas/{empId}/asignaciones`
 *      → se copian a `empresas/{empId}/tareas` con schema unificado
 *
 * INSTRUCCIONES:
 *   1. Abrí la app en el browser, logueate como admin
 *   2. Abrí la consola del navegador (F12 → Console)
 *   3. Ejecutá: await window.__migrarTareasUnificadas()
 *   4. Revisá el output. Si OK, ejecutá: await window.__migrarTareasUnificadas({ dryRun: false })
 *
 * El script tiene dos modos:
 *   - dryRun: true  (default) → solo lee y reporta, no escribe nada
 *   - dryRun: false            → ejecuta la migración real
 *
 * ═══════════════════════════════════════════════════════════════
 */

import {
  collection, collectionGroup, getDocs, getDoc, doc, setDoc,
  query, where, serverTimestamp, writeBatch,
} from 'firebase/firestore'
import { db } from '../src/core/lib/firebase'

// ─── Helpers ───

function esTareaNosCatalogo(data) {
  // Una tarea tiene al menos uno de estos campos; un catálogo tiene nombre/objetivo/ejecutores
  return !!(
    data.estado ||
    data.responsableUserId ||
    data.emisorUserId ||
    data.asignadoA ||
    data.asignadoPor ||
    data.origen?.tipo === 'mensaje'
  )
}

function normalizarEstado(estado) {
  const map = {
    'completado': 'cerrado',
    'completada': 'cerrado',
    'en_progreso': 'en_curso',
  }
  return map[estado] || estado || 'pendiente'
}

function normalizarAsignacion(data) {
  // Mapea campos del schema viejo de asignaciones al schema unificado
  return {
    titulo: data.titulo || '',
    descripcion: data.descripcion || '',
    estado: normalizarEstado(data.estado),
    emisorUserId: data.asignadoPor || data.emisorUserId || null,
    responsableUserId: data.asignadoA || data.responsableUserId || null,
    empresaId: data.empresaId || data._empresaId || null,
    prioridad: data.prioridad || 'normal',
    vencimiento: data.deadline || data.vencimiento || null,
    // Preservar campos adicionales del sistema de asignaciones
    area: data.area || null,
    tipoAccion: data.tipoAccion || null,
    recursos: data.recursos || [],
    delegadoA: data.delegadoA || null,
    delegadoPor: data.delegadoPor || null,
    // Origen para trazabilidad
    origen: data.origen || { tipo: 'manual' },
    // Timestamps
    creadoEn: data.creadoEn || null,
    actualizadoEn: data.actualizadoEn || null,
    // Marca de migración
    _migradoDesde: 'asignaciones',
    _migradoEn: new Date().toISOString(),
  }
}

function normalizarAccionable(data) {
  return {
    titulo: data.titulo || data.descripcion?.slice(0, 80) || 'Sin título',
    descripcion: data.descripcion || '',
    estado: normalizarEstado(data.estado),
    emisorUserId: data.emisorUserId || null,
    responsableUserId: data.responsableUserId || null,
    empresaId: data.empresaId || null,
    prioridad: data.prioridad || 'normal',
    vencimiento: data.vencimiento || data.fechaLimite || null,
    slaId: data.slaId || null,
    origen: data.origen || { tipo: 'manual' },
    // Timestamps
    creadoEn: data.creadoEn || null,
    actualizadoEn: data.actualizadoEn || null,
    // Marca de migración
    _migradoDesde: 'accionables_toplevel',
    _migradoEn: new Date().toISOString(),
  }
}

// ─── Script principal ───

async function migrarTareasUnificadas({ dryRun = true } = {}) {
  console.log(`\n${'═'.repeat(60)}`)
  console.log(`  MIGRACIÓN TAREAS UNIFICADAS`)
  console.log(`  Modo: ${dryRun ? '🔍 DRY RUN (solo lectura)' : '🚀 EJECUCIÓN REAL'}`)
  console.log(`${'═'.repeat(60)}\n`)

  const stats = {
    accionablesTareas: 0,
    accionablesCatalogo: 0,
    accionablesSinEmpresa: 0,
    accionablesYaExisten: 0,
    accionablesMigrados: 0,
    asignacionesTotal: 0,
    asignacionesYaExisten: 0,
    asignacionesMigradas: 0,
    errores: [],
  }

  // ── 1. Leer accionables top-level ──
  console.log('📋 Leyendo colección top-level `accionables`...')
  const accSnap = await getDocs(collection(db, 'accionables'))
  console.log(`   → ${accSnap.size} documentos encontrados`)

  const tareasParaMigrar = []
  for (const d of accSnap.docs) {
    const data = d.data()
    if (esTareaNosCatalogo(data)) {
      stats.accionablesTareas++
      if (!data.empresaId) {
        stats.accionablesSinEmpresa++
        console.warn(`   ⚠️  Tarea ${d.id} sin empresaId — se omite`)
        continue
      }
      tareasParaMigrar.push({ id: d.id, data, empresaId: data.empresaId })
    } else {
      stats.accionablesCatalogo++
    }
  }
  console.log(`   → ${stats.accionablesTareas} son tareas, ${stats.accionablesCatalogo} son catálogo`)
  console.log(`   → ${stats.accionablesSinEmpresa} tareas sin empresaId (omitidas)`)

  // ── 2. Leer asignaciones (subcollección vieja) ──
  console.log('\n📋 Leyendo subcollecciones `empresas/*/asignaciones`...')
  const asigSnap = await getDocs(collectionGroup(db, 'asignaciones'))
  stats.asignacionesTotal = asigSnap.size
  console.log(`   → ${asigSnap.size} asignaciones encontradas`)

  const asignacionesParaMigrar = []
  for (const d of asigSnap.docs) {
    const empresaId = d.ref.parent.parent?.id
    if (!empresaId) {
      console.warn(`   ⚠️  Asignación ${d.id} sin empresaId en path — se omite`)
      continue
    }
    asignacionesParaMigrar.push({ id: d.id, data: d.data(), empresaId })
  }

  // ── 3. Verificar cuáles ya existen en destino (SOLO subcollección) ──
  console.log('\n🔍 Verificando destino `empresas/*/tareas`...')

  // Leer todo lo que ya existe en destino — filtrando SOLO subcollección
  // (collectionGroup devuelve también la top-level 'accionables', hay que excluirla)
  const existenteSnap = await getDocs(collectionGroup(db, 'tareas'))
  const existenteIds = new Set()
  for (const d of existenteSnap.docs) {
    // Solo contar docs que están en subcollección empresas/{empId}/tareas
    // (tienen parent.parent con id de empresa)
    const parentId = d.ref.parent.parent?.id
    const grandparentCol = d.ref.parent.parent?.parent?.id
    if (!parentId || grandparentCol !== undefined) {
      // Es subcollección: empresas/{empId}/tareas/{accId}
      // ref.parent = tareas collection, ref.parent.parent = empresas/{empId} doc
      // Verificamos que el path tenga profundidad correcta
      const pathSegments = d.ref.path.split('/')
      if (pathSegments.length >= 4 && pathSegments[0] === 'empresas') {
        existenteIds.add(d.id)
        const data = d.data()
        if (data._migradoDesdeId) existenteIds.add(data._migradoDesdeId)
      }
    }
  }
  console.log(`   → ${existenteIds.size} docs ya existen en destino (subcollección)`)

  // ── 4. Filtrar los que ya existen ──
  const accionablesNuevos = tareasParaMigrar.filter(t => {
    if (existenteIds.has(t.id)) {
      stats.accionablesYaExisten++
      return false
    }
    return true
  })

  const asignacionesNuevas = asignacionesParaMigrar.filter(a => {
    if (existenteIds.has(a.id) || existenteIds.has(`asig_${a.id}`)) {
      stats.asignacionesYaExisten++
      return false
    }
    return true
  })

  console.log(`\n📊 Resumen pre-migración:`)
  console.log(`   Accionables (tareas) a migrar: ${accionablesNuevos.length} (${stats.accionablesYaExisten} ya existen)`)
  console.log(`   Asignaciones a migrar: ${asignacionesNuevas.length} (${stats.asignacionesYaExisten} ya existen)`)
  console.log(`   Total a escribir: ${accionablesNuevos.length + asignacionesNuevas.length}`)

  if (dryRun) {
    console.log('\n🔍 DRY RUN — no se escribió nada.')
    console.log('   Para ejecutar la migración real:')
    console.log('   await window.__migrarTareasUnificadas({ dryRun: false })')

    // Mostrar preview
    if (accionablesNuevos.length > 0) {
      console.log('\n   Preview accionables:')
      accionablesNuevos.slice(0, 5).forEach(t => {
        console.log(`     → ${t.id} (empresa: ${t.empresaId}) — "${t.data.titulo || t.data.descripcion?.slice(0, 40) || '?'}"`)
      })
      if (accionablesNuevos.length > 5) console.log(`     ... y ${accionablesNuevos.length - 5} más`)
    }
    if (asignacionesNuevas.length > 0) {
      console.log('\n   Preview asignaciones:')
      asignacionesNuevas.slice(0, 5).forEach(a => {
        console.log(`     → ${a.id} (empresa: ${a.empresaId}) — "${a.data.titulo || '?'}"`)
      })
      if (asignacionesNuevas.length > 5) console.log(`     ... y ${asignacionesNuevas.length - 5} más`)
    }

    return stats
  }

  // ── 5. EJECUTAR MIGRACIÓN ──
  console.log('\n🚀 Ejecutando migración...')

  // Usar batched writes (max 500 por batch)
  const BATCH_SIZE = 400
  let batch = writeBatch(db)
  let batchCount = 0

  async function flushBatch() {
    if (batchCount > 0) {
      await batch.commit()
      batch = writeBatch(db)
      batchCount = 0
    }
  }

  // 5a. Migrar accionables top-level → subcollección
  for (const t of accionablesNuevos) {
    try {
      const destRef = doc(db, 'empresas', t.empresaId, 'tareas', t.id)
      const normalized = normalizarAccionable(t.data)
      normalized._migradoDesdeId = t.id
      batch.set(destRef, normalized)
      batchCount++
      stats.accionablesMigrados++

      if (batchCount >= BATCH_SIZE) await flushBatch()
    } catch (e) {
      stats.errores.push({ tipo: 'accionable', id: t.id, error: e.message })
      console.error(`   ❌ Error migrando accionable ${t.id}:`, e)
    }
  }

  // 5b. Migrar asignaciones → subcollección tareas
  for (const a of asignacionesNuevas) {
    try {
      // Usar prefix para evitar colisión de IDs
      const newId = `asig_${a.id}`
      const destRef = doc(db, 'empresas', a.empresaId, 'tareas', newId)
      const normalized = normalizarAsignacion({ ...a.data, empresaId: a.empresaId })
      normalized._migradoDesdeId = a.id
      batch.set(destRef, normalized)
      batchCount++
      stats.asignacionesMigradas++

      if (batchCount >= BATCH_SIZE) await flushBatch()
    } catch (e) {
      stats.errores.push({ tipo: 'asignacion', id: a.id, error: e.message })
      console.error(`   ❌ Error migrando asignación ${a.id}:`, e)
    }
  }

  // Flush final
  await flushBatch()

  // ── 6. Reporte final ──
  console.log(`\n${'═'.repeat(60)}`)
  console.log(`  MIGRACIÓN COMPLETADA`)
  console.log(`${'═'.repeat(60)}`)
  console.log(`  Accionables migrados: ${stats.accionablesMigrados}`)
  console.log(`  Asignaciones migradas: ${stats.asignacionesMigradas}`)
  console.log(`  Total escritos: ${stats.accionablesMigrados + stats.asignacionesMigradas}`)
  if (stats.errores.length > 0) {
    console.log(`  ❌ Errores: ${stats.errores.length}`)
    stats.errores.forEach(e => console.log(`     ${e.tipo} ${e.id}: ${e.error}`))
  } else {
    console.log(`  ✅ Sin errores`)
  }
  console.log(`\n  PRÓXIMO PASO: verificar los datos en Firestore.`)
  console.log(`  Si todo OK, las colecciones viejas se pueden limpiar manualmente.`)

  return stats
}

// Exponer en window
if (typeof window !== 'undefined') {
  window.__migrarTareasUnificadas = migrarTareasUnificadas
}

export { migrarTareasUnificadas }
